import type { TFunction } from 'i18next';
import type { ContentKind, ContentState } from '@wix/ai-scheduling-assistant-common-lib/utils';
import type { CalendarItem } from './calendar-item';
import { editSeedWhen, type CalendarFormatParts } from './calendar-format';
import type { BadgeVariant } from '../../components/symphony/ui/Badge';

// The words an item carries beyond its title, shared by the grid, the agenda and the details.

export function itemTitle(item: CalendarItem, t: TFunction): string {
  if (item.busy) return t('calendar.item.busy');
  if (item.title) return item.title;
  return t(item.kind === 'meeting' ? 'calendar.item.untitledMeeting' : 'calendar.item.untitled');
}

/**
 * The owner's message when a tap or a drag hands an item to Maestro: its kind and when, never its title. The card is drawn
 * in place of these words, and a meeting's title is written by whoever sent the invite, so it reaches Maestro only as data
 * in the hand-off rather than as the owner's own words.
 */
export function handOffSeed(item: Pick<CalendarItem, 'kind' | 'allDay' | 'start' | 'end'>, t: TFunction, parts: CalendarFormatParts): string {
  return t('calendar.open.seed', { kind: t(`calendar.card.kind.${item.kind}`), when: editSeedWhen(parts, item) });
}

/** What a screen reader says for an item on the grid: its kind first, since the colour and mark don't reach it. */
export function itemAccessibilityLabel(item: CalendarItem, t: TFunction, when: string): string {
  return [t(`calendar.kind.${item.kind}s`), itemTitle(item, t), when].join(', ');
}

/** One line under the title: who and where for a booking, the account for a meeting. */
export function itemMeta(item: CalendarItem, t: TFunction, spaceName?: string): string {
  const parts: string[] = [];
  if (item.kind === 'booking') {
    if (item.capacity) parts.push(t('calendar.item.places', { booked: item.capacity.booked, total: item.capacity.total }));
    else if (item.people?.[0]?.name) parts.push(item.people[0].name);
    if (item.staff?.length) parts.push(item.staff.join(', '));
    if (item.sourceLabel) parts.push(item.sourceLabel);
  } else if (item.kind === 'meeting') {
    if (item.location) parts.push(item.location);
    if (item.sourceLabel) parts.push(item.sourceLabel);
  } else {
    if (item.kind === 'task' && (item.routineName || item.agentName)) parts.push(item.routineName || item.agentName || '');
    if (spaceName) parts.push(spaceName);
    if (item.kind === 'task' && item.goalTitle) parts.push(item.goalTitle);
    if (item.kind === 'routine') parts.push(t(item.repeatsDaily ? 'calendar.item.everyDay' : 'calendar.item.routineRun'));
  }
  return parts.join(' · ');
}

/** The status words an item shows, each in the Badge style the rest of the app gives that state. */
export function itemTags(item: CalendarItem, t: TFunction): { label: string; variant: BadgeVariant }[] {
  const tags: { label: string; variant: BadgeVariant }[] = [];
  if (item.needsYou) {
    tags.push({ label: t(item.kind === 'booking' ? 'calendar.status.pendingApproval' : 'calendar.status.needsYou'), variant: 'waiting-on-you' });
  }
  if (item.status === 'cancelled') tags.push({ label: t('calendar.status.cancelled'), variant: 'paused' });
  if (item.status === 'declined') tags.push({ label: t('calendar.status.declined'), variant: 'paused' });
  const postTag = item.kind === 'task' && item.post?.state ? postTagOf(item.post.kind, item.post.state) : undefined;
  if (postTag) {
    // A post's state says more than its task's status, and says it once: "Needs you" is already there when it is.
    if (!(item.needsYou && postTag.key === 'calendar.status.needsYou')) tags.push({ label: t(postTag.key), variant: postTag.variant });
  } else {
    if ((item.kind === 'task' && item.status === 'done') || (item.kind === 'routine' && item.done)) tags.push({ label: t('calendar.status.done'), variant: 'done' });
    if (item.kind === 'task' && item.status === 'failed') tags.push({ label: t('calendar.status.failed'), variant: 'failed' });
    if (item.kind === 'task' && item.status === 'inProgress') tags.push({ label: t('calendar.status.inProgress'), variant: 'in-progress' });
    if (item.kind === 'task' && item.status === 'scheduled')
      tags.push({ label: t('routines.schedule.scheduled'), variant: 'waiting-on-a-reply' });
  }
  if (item.kind === 'meeting' && item.rsvp === 'pending') tags.push({ label: t('calendar.rsvp.pending'), variant: 'waiting-on-a-reply' });
  if (item.kind === 'meeting' && item.rsvp === 'tentative') tags.push({ label: t('calendar.rsvp.tentative'), variant: 'waiting-on-a-reply' });
  tags.push(...bookingTags(item, t));
  return tags;
}

/** A task's post state as its one badge, in the words the task statuses already use where they mean the same.
 *  A dropped or failed post never reaches the calendar, so neither has a tag. */
const POST_TAG: Partial<Record<ContentState, { key: string; variant: BadgeVariant }>> = {
  idea: { key: 'calendar.post.idea', variant: 'paused' },
  drafting: { key: 'calendar.post.drafting', variant: 'in-progress' },
  working: { key: 'calendar.post.working', variant: 'in-progress' },
  ready: { key: 'calendar.status.needsYou', variant: 'waiting-on-you' },
  scheduled: { key: 'routines.schedule.scheduled', variant: 'waiting-on-a-reply' },
};

const DONE_TAG: Record<ContentKind, string> = {
  social: 'calendar.post.published',
  email: 'calendar.post.sent',
  blog: 'calendar.post.blogPublished',
};

function postTagOf(kind: ContentKind, state: ContentState): { key: string; variant: BadgeVariant } | undefined {
  return state === 'done' ? { key: DONE_TAG[kind], variant: 'done' } : POST_TAG[state];
}

const PAYMENT_TAG: Record<string, { key: string; variant: BadgeVariant }> = {
  paid: { key: 'calendar.booking.paid', variant: 'done' },
  not_paid: { key: 'calendar.booking.notPaid', variant: 'waiting-on-you' },
  partially_paid: { key: 'calendar.booking.partlyPaid', variant: 'in-progress' },
  refunded: { key: 'calendar.booking.refunded', variant: 'paused' },
};

const ATTENDANCE_TAG: Record<string, { key: string; variant: BadgeVariant }> = {
  attended: { key: 'calendar.booking.checkedIn', variant: 'done' },
  no_show: { key: 'calendar.booking.noShow', variant: 'failed' },
};

/**
 * One client's payment and attendance as badges; for a class, the count still owing rather than a badge
 * per person. The spec's appointment chips: paid or not, checked in, no-show, waiting list.
 */
function bookingTags(item: CalendarItem, t: TFunction): { label: string; variant: BadgeVariant }[] {
  const clients = (item.booking?.clients ?? []).filter((client) => client.status !== 'canceled' && client.status !== 'declined');
  if (clients.length === 0) return [];
  if (clients.length > 1) {
    const owing = clients.filter((client) => client.payment === 'not_paid' || client.payment === 'partially_paid').length;
    const waiting = clients.filter((client) => client.status === 'waiting_list').length;
    const checkedIn = clients.filter((client) => client.attendance === 'attended').length;
    const noShows = clients.filter((client) => client.attendance === 'no_show').length;
    return [
      ...(waiting ? [{ label: t('calendar.booking.waitingListCount', { count: waiting }), variant: 'paused' as const }] : []),
      ...(checkedIn ? [{ label: t('calendar.booking.checkedInCount', { count: checkedIn }), variant: 'done' as const }] : []),
      ...(noShows ? [{ label: t('calendar.booking.noShowCount', { count: noShows }), variant: 'failed' as const }] : []),
      ...(owing ? [{ label: t('calendar.booking.owingCount', { count: owing }), variant: 'waiting-on-you' as const }] : []),
    ];
  }
  const [client] = clients;
  const tags: { label: string; variant: BadgeVariant }[] = [];
  if (client.status === 'waiting_list') tags.push({ label: t('calendar.booking.waitingList'), variant: 'paused' });
  if (client.payment && PAYMENT_TAG[client.payment])
    tags.push({ label: t(PAYMENT_TAG[client.payment].key), variant: PAYMENT_TAG[client.payment].variant });
  if (client.attendance && ATTENDANCE_TAG[client.attendance]) {
    tags.push({ label: t(ATTENDANCE_TAG[client.attendance].key), variant: ATTENDANCE_TAG[client.attendance].variant });
  }
  return tags;
}
